/**
 * File: Home.jsx
 * Path: /src/pages/Home.jsx
 * Description: Main homepage with modern styling components
 * Key Features: Using modular components for consistent styling
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/home/Hero';
import Features from '../components/home/Features';
import Solutions from '../components/home/Solutions';
import ModernCtaSection from '../components/common/ModernCtaSection';
import ModernStatsSection from '../components/common/ModernStatsSection';
import useScrollAnimation from '../hooks/useScrollAnimation';
import Button from '../components/common/Button';

const Home = () => {
  // Initialize scroll animations
  useScrollAnimation();

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Hero />
      
      {/* Stats Section - Using ModernStatsSection instead of hardcoded section */}
      <ModernStatsSection stats={[
        { number: '85%', label: 'Time Saved on Research' },
        { number: '70%', label: 'Faster Contract Analysis' },
        { number: '92%', label: 'Client Satisfaction' },
        { number: '3x', label: 'Return on Investment' }
      ]} />
      
      <Features />
      
      {/* CTA Banner - Using ModernCtaSection instead of hardcoded section */}
      <ModernCtaSection
        title="Ready to Revolutionize Your Legal Practice?"
        description="Join the growing number of forward-thinking law firms using Veritect.ai to gain a competitive edge."
        buttonText="Schedule a Demo"
        buttonLink="/contact"
      />
      
      <Solutions />
      
      {/* Featured Solution Section - Highlighting Legal Research */}
      <section className="py-20 bg-dark-50 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/5 to-secondary-900/5 z-0"></div>
        <div className="absolute inset-0 opacity-5 z-0">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-primary-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-secondary-600/5 rounded-full blur-3xl"></div>
        
        <div className="container-padding mx-auto relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="order-2 lg:order-1"
              >
                <span className="text-primary-600 font-semibold">Featured Solution</span>
                <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                  Transform Your <span className="gradient-text">Legal Research</span> with AI
                </h2>
                <p className="text-dark-600 mb-6">
                  Our AI-powered legal research platform analyzes millions of cases, statutes, and legal documents in seconds to find the most relevant precedents and arguments for your case.
                </p>
                <p className="text-dark-600 mb-8">
                  With natural language queries, automated brief generation, and citation validation, Veritect.ai's Legal Research solution helps you work smarter, faster, and with greater insight.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button variant="primary" to="/solutions/legal-research">
                    Explore Legal Research AI
                  </Button>
                  <Button variant="outline" to="/contact">
                    Request a Demo
                  </Button>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="order-1 lg:order-2"
              >
                {/* Legal Research Interface Preview */}
                <div className="relative">
                  <div className="rounded-xl overflow-hidden shadow-xl">
                    <div className="bg-gradient-to-br from-primary-700 to-secondary-700 p-6">
                      <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 text-white">
                        {/* Search Interface */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center mr-4">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                            </div>
                            <h3 className="font-bold text-xl">Veritect Legal Research AI</h3>
                          </div>
                          <div className="flex space-x-2">
                            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                          </div>
                        </div>
                        
                        <div className="relative mb-4">
                          <input 
                            type="text" 
                            className="w-full bg-white/5 border border-white/20 rounded-lg p-3 pr-12 text-white placeholder-white/50" 
                            placeholder="Find cases on implied warranties in software licensing..."
                            disabled
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center animate-pulse">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        {/* Results Preview */}
                        <div className="bg-white/5 rounded-lg p-3 mb-4">
                          <div className="text-xs text-white/60 mb-2">Top Results</div>
                          <ul className="text-sm space-y-2">
                            <li className="flex items-start">
                              <div className="w-4 h-4 rounded-full bg-accent-500/30 text-white flex items-center justify-center mt-0.5 mr-2 text-xs">1</div>
                              <div>
                                <div className="font-medium">Vernor v. Autodesk, Inc.</div>
                                <div className="text-white/70 text-xs">621 F.3d 1102 (9th Cir. 2010)</div>
                              </div>
                            </li>
                            <li className="flex items-start">
                              <div className="w-4 h-4 rounded-full bg-accent-500/30 text-white flex items-center justify-center mt-0.5 mr-2 text-xs">2</div>
                              <div>
                                <div className="font-medium">Specht v. Netscape Communications Corp.</div>
                                <div className="text-white/70 text-xs">306 F.3d 17 (2d Cir. 2002)</div>
                              </div>
                            </li>
                          </ul>
                        </div>
                        
                        {/* Progress Indicators */}
                        <div className="flex space-x-4">
                          <div className="flex-1 bg-white/5 rounded-lg p-2">
                            <div className="text-xs text-white/60 mb-1">Relevance</div>
                            <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                              <div className="w-5/6 h-1.5 bg-accent-500 rounded-full"></div>
                            </div>
                          </div>
                          <div className="flex-1 bg-white/5 rounded-lg p-2">
                            <div className="text-xs text-white/60 mb-1">Cases Analyzed</div>
                            <div className="text-xs font-medium">5,428</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating elements for visual interest */}
                  <div className="absolute -top-6 -right-6 w-12 h-12 bg-primary-100 rounded-lg rotate-12 flex items-center justify-center z-20">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Final CTA - Using ModernCtaSection with custom content */}
      <ModernCtaSection
        title="See Our Solutions in Action"
        description="Book a personalized demo to see how our AI-powered solutions can transform your legal practice."
      >
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button 
            variant="primary" 
            size="lg"
            to="/contact"
            className="bg-blue-600 hover:bg-blue-700 shadow-lg relative overflow-hidden group"
          >
            <span className="relative z-10">Book a Demo</span>
            <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
          </Button>
          <Button 
            variant="outline" 
            size="lg"
            to="/solutions/legal-research"
            className="text-white border-white hover:bg-white/10"
          >
            Explore Legal Research
          </Button>
        </div>
      </ModernCtaSection>
    </motion.div>
  );
};

export default Home;